import {
  Button,
  Heading,
  Icon,
  LoadingIcon,
  Stack,
  Text,
} from '@eluve/components';
import { useTranslation } from '@eluve/i18n';

export type DocGenState =
  | { status: 'preparing'; title: string }
  | { status: 'error'; title: string; message?: string };

export type DocumentGeneratingTabContentProps = {
  state: DocGenState;
  onStop: () => void;
  onRetry: () => void;
};

/**
 * Full-tab "Preparing your document" view shown while a markdown document is
 * generated — the markdown counterpart to PdfFillTabContent's preparing state,
 * so both generation paths share one experience.
 */
export const DocumentGeneratingTabContent: React.FC<
  DocumentGeneratingTabContentProps
> = ({ state, onStop, onRetry }) => {
  const { t } = useTranslation();
  if (state.status === 'error') {
    return (
      <Stack fullHeight justify="center" align="center" gap={4}>
        <Stack align="center" gap={4}>
          <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-bg-tertiary">
            <Icon
              name="AlertCircle"
              size="2xl"
              className="text-text-negative"
            />
          </div>
          <Stack align="center" gap={2}>
            <Heading size="xl">
              {t('appointmentTools.documents.generateError')}
            </Heading>
            {state.message && (
              <Text color="secondary" className="text-center">
                {state.message}
              </Text>
            )}
          </Stack>
          <Stack align="center" gap={2}>
            <Button onClick={onRetry}>
              <Icon name="RotateCcw" size="auto" />
              {t('appointmentTools.common.tryAgain')}
            </Button>
            <Button variant="ghost" size="sm" onClick={onStop}>
              {t('appointmentTools.common.close')}
            </Button>
          </Stack>
        </Stack>
      </Stack>
    );
  }

  return (
    <Stack fullHeight justify="center" align="center" gap={4}>
      <Stack align="center" gap={4}>
        <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-bg-tertiary">
          <Icon name="FileText" size="2xl" className="text-text-secondary" />
        </div>
        <Stack align="center" gap={2}>
          <Heading size="xl">
            {t('appointmentTools.documents.preparingDocument')}
          </Heading>
          <Text color="secondary" className="text-center">
            {t('appointmentTools.documents.docFromSession')}
          </Text>
          <Text size="sm" color="tertiary" className="text-center">
            {t('appointmentTools.documents.switchTabsHint')}
          </Text>
        </Stack>
        <Stack align="center" gap={2}>
          <Button disabled>
            <LoadingIcon />
            {t('appointmentTools.documents.generating')}
          </Button>
          <Button variant="ghost" size="sm" onClick={onStop}>
            {t('appointmentTools.documents.stopGenerating')}
          </Button>
        </Stack>
      </Stack>
    </Stack>
  );
};
